if (!customElements.get('product-lifestyle-gallery')) {
  customElements.define('product-lifestyle-gallery', class extends HTMLElement {
    constructor() {
      super()
      this.mount = this.mount.bind(this)
      this.updateNavigation = this.updateNavigation.bind(this)
      this.resizeCarousel = this.resizeCarousel.bind(this)
      this.carouselVersion = 0
    }

    connectedCallback() {
      const hasProduct = typeof getStore === 'function' && getStore('product')?.id
      if (document.readyState !== 'loading' && hasProduct) this.mount()
      else document.addEventListener('DOMContentLoaded', this.mount, { once: true })
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.mount)
      this.resizeObserver?.disconnect()
      this.destroyCarousel()
      this.app?.unmount()
      this.querySelector('[data-lifestyle-content]')?.remove()
      this.app = null
      this.imageSignature = null
      this.carouselWidth = null
    }

    mount() {
      if (this.app || !this.isConnected || !getStore('product')) return

      const element = this
      const product = getStore('product')
      this.append(this.querySelector('template').content.cloneNode(true))
      this.slider = this.querySelector('.product-lifestyle-gallery-section__media')
      this.state = PetiteVue.reactive({
        productTitle: this.dataset.productTitle,
        previousDisabled: true,
        nextDisabled: true,
        hasOverflow: false,
        images: [],
        get sourceImages() {
          if (String(product.id) !== element.dataset.productId || !product.isHydrated) return []
          return product.lifestyleImages
        },
        refresh(images) {
          element.refresh(images)
        },
        scroll(direction) {
          element.scroll(direction)
        },
        imageText(image, property) {
          const { objects } = Shopify.ImageAltData(image.alt)
          const value = objects.find(metadata => Object.prototype.hasOwnProperty.call(metadata, property))?.[property]
          return typeof value === 'string' ? value.trim() : ''
        },
        imageAlt(image) {
          const caption = this.imageText(image, 'caption')
          if (caption) return caption
          const alt = typeof image.alt === 'string' ? image.alt : ''
          return alt && !alt.includes('{') ? alt : element.dataset.productTitle
        },
        open(image) {
          const images = product.lifestyleImages
          getStore('modal')?.open('pdp-gallery', {
            images,
            selectedImage: images.findIndex(item => item.src === image.src)
          })
        }
      })
      this.app = PetiteVue.createApp({
        $delimiters: ['${', '}'],
        Shopify,
        gallery: this.state
      })
      this.app.mount(this.querySelector('[data-lifestyle-content]'))
      this.resizeObserver = new ResizeObserver(this.resizeCarousel)
      this.resizeObserver.observe(this.slider)
    }

    refresh(images) {
      this.updateVisibility(images.length)
      const signature = JSON.stringify(images.map(image => [image.src, image.alt, image.width, image.height]))
      if (signature === this.imageSignature) return
      this.imageSignature = signature
      // Restore the cells before Petite Vue updates its list; Flickity moves them into a viewport.
      this.destroyCarousel()
      this.state.images = images
      if (!images.length) return
      const version = this.carouselVersion
      this.frame = requestAnimationFrame(() => {
        loadFlickity(() => {
          if (!this.isConnected || !this.app || version !== this.carouselVersion) return
          this.flickity = new Flickity(this.slider, {
            cellSelector: '.three-block-feature__slide',
            cellAlign: 'left',
            contain: true,
            groupCells: false,
            prevNextButtons: false,
            pageDots: false,
            wrapAround: false
          })
          this.flickity.on('select', this.updateNavigation)
          this.flickity.on('settle', this.updateNavigation)
          this.flickity.on('staticClick', (event, _pointer, _cell, index) => {
            const image = this.state.images[index]
            if (image && event.target.closest('.product-lifestyle-gallery-section__image-button')) {
              this.state.open(image)
            }
          })
          this.carouselWidth = this.slider.clientWidth
          this.updateNavigation()
        })
      })
    }

    destroyCarousel() {
      this.carouselVersion += 1
      cancelAnimationFrame(this.frame)
      this.flickity?.destroy()
      this.flickity = null
    }

    resizeCarousel() {
      const width = this.slider.clientWidth
      if (width === this.carouselWidth) return
      this.carouselWidth = width
      if (width && this.flickity) {
        this.flickity.resize()
        this.updateNavigation()
      }
    }

    scroll(direction) {
      if (!this.flickity?.selectedSlide) return
      const { slides, selectedIndex, selectedSlide } = this.flickity
      let index = selectedIndex + direction
      // Contained cells at the end can share a position. Skip them so each arrow click moves.
      while (slides[index] && Math.abs(slides[index].target - selectedSlide.target) < 1) index += direction
      if (slides[index]) this.flickity.select(index)
    }

    updateNavigation() {
      if (!this.flickity?.selectedSlide) return
      this.syncSlideAccessibility()
      const { slides, selectedSlide } = this.flickity
      const first = slides[0].target
      const last = slides[slides.length - 1].target
      this.state.hasOverflow = last - first > 1
      this.state.previousDisabled = selectedSlide.target <= first + 1
      this.state.nextDisabled = selectedSlide.target >= last - 1
      if (this.flickity.options.draggable !== this.state.hasOverflow) {
        this.flickity.options.draggable = this.state.hasOverflow
        this.flickity.updateDraggable()
      }
    }

    syncSlideAccessibility() {
      const viewport = this.flickity.viewport.getBoundingClientRect()
      // Read geometry together before changing accessibility attributes.
      const visibility = this.flickity.cells.map(({ element }) => {
        const rect = element.getBoundingClientRect()
        const visible = rect.right > viewport.left + 1 && rect.left < viewport.right - 1
        return { element, visible }
      })
      visibility.forEach(({ element, visible }) => {
        const hidden = String(!visible)
        if (element.getAttribute('aria-hidden') !== hidden) element.setAttribute('aria-hidden', hidden)
        if (element.inert !== !visible) element.inert = !visible
      })
    }

    updateVisibility(imageCount = this.state?.images.length ?? 0) {
      this.hidden = !imageCount && !Shopify.designMode
    }
  })
}
